import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, date, dateTime, formatError, money } from "../../lib/api";
import { Modal } from "../../components/Shell";

const useRecords = (path) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get(path).then((r) => setRows(r.data)).catch((e) => { toast.error(formatError(e)); setRows([]); }), [path]);
  useEffect(() => { load(); }, [load]);
  return [rows, load];
};

const Table = ({ title, rows, columns, testId, empty }) => {
  const [q, setQ] = useState("");
  if (!rows) return <div className="loading">Loading…</div>;
  const shown = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="section-title"><h2>{title}</h2><input className="search" data-testid={`${testId}-search-input`} placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      {!shown.length ? <div className="empty-state" data-testid={`${testId}-empty-state`}>{empty}</div> :
        <div className="table-wrap"><table className="admin-table" data-testid={`${testId}-table`}><thead><tr>{columns.map(([h]) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{shown.map((r, i) => <tr key={i} data-testid={`${testId}-row-${i}`}>{columns.map(([h, render]) => <td key={h}>{render(r)}</td>)}</tr>)}</tbody></table></div>}
    </div>
  );
};

export const markRefund = async (orderId, onDone) => {
  const note = window.prompt("Refund the payment in your Razorpay Dashboard first, then enter a note (e.g. Razorpay refund ID):");
  if (!note) return;
  try { await api.post(`/admin/orders/${orderId}/refund`, { note }); toast.success("Order marked as refunded"); onDone(); } catch (e) { toast.error(formatError(e)); }
};

export const PaymentsRecords = ({ mode }) => {
  const [rows, load] = useRecords("/admin/payments");
  return <>
    {mode && <div className={mode === "MOCK" ? "mock-banner" : "live-banner"} data-testid="payments-mode-banner">{mode === "MOCK" ? "MOCK MODE: no real money is collected." : "RAZORPAY ACTIVE: payments are verified by signature on the server and reconciled with Razorpay if a confirmation is missed. Process refunds in the Razorpay Dashboard, then mark them here."}</div>}
    <Table title="Payments" testId="payments" rows={rows} empty="No orders yet." columns={[
    ["Order", (r) => <code>{r.merchant_order_id}</code>], ["Learner", (r) => r.user_email], ["Item", (r) => `${r.kind} · ${r.certification_title || ""}`],
    ["Amount", (r) => money(r.amount_minor)], ["Mode", (r) => <span className={`pill ${r.mode === "MOCK" ? "draft" : "published"}`}>{r.mode}</span>],
    ["Status", (r) => <><span className={`pill ${r.status === "paid" ? "published" : "draft"}`}>{r.status}</span>{r.needs_refund && <span className="pill failed" data-testid={`needs-refund-${r.merchant_order_id}`}>refund due</span>}</>], ["Created", (r) => date(r.created_at)],
    ["", (r) => r.status === "paid" && <button className="button outline compact" data-testid={`refund-order-${r.merchant_order_id}`} onClick={() => markRefund(r.merchant_order_id, load)}>Mark refunded</button>]]} />
  </>;
};

export const ResultsRecords = () => {
  const [rows] = useRecords("/admin/results");
  return <Table title="Assessment results" testId="results" rows={rows} empty="No attempts yet." columns={[
    ["Learner", (r) => <><b>{r.user_name}</b><small>{r.user_email}</small></>], ["Certification", (r) => r.certification_title],
    ["Score", (r) => `${r.score}% (${r.correct}/${r.total})`], ["Result", (r) => <span className={`pill ${r.passed ? "published" : "failed"}`}>{r.passed ? "passed" : r.timed_out ? "timed out" : "not passed"}</span>],
    ["Submitted", (r) => date(r.submitted_at)]]} />;
};

export const CertificatesRecords = () => {
  const [rows, load] = useRecords("/admin/certificates");
  const [revoking, setRevoking] = useState(null);
  const [reason, setReason] = useState("");
  const resend = async (id) => {
    try { const r = await api.post(`/admin/certificates/${id}/resend-email`); toast[r.data?.status === "sent" ? "success" : "error"](`Email ${r.data?.status}`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const revoke = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/certificates/${revoking}/revoke`, { reason }); toast.success("Certificate revoked"); setRevoking(null); setReason(""); load(); } catch (err) { toast.error(formatError(err)); }
  };
  const reinstate = async (id) => {
    if (!window.confirm(`Reinstate ${id} as valid?`)) return;
    try { await api.post(`/admin/certificates/${id}/reinstate`); toast.success("Certificate reinstated"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return <>
    <Table title="Issued certificates" testId="certificates" rows={rows} empty="No certificates issued yet." columns={[
    ["ID", (r) => <Link className="text-link" data-testid={`admin-cert-link-${r.certificate_id}`} to={`/certificate/${r.certificate_id}`}>{r.certificate_id}</Link>],
    ["Learner", (r) => <><b>{r.learner_name}</b><small>{r.user_email}</small></>], ["Certification", (r) => r.certification_title],
    ["Status", (r) => <span title={r.revoked_reason || ""}><span className={`pill ${r.status === "valid" ? "published" : "failed"}`} data-testid={`cert-status-${r.certificate_id}`}>{r.status}</span>{r.revoked_reason && <small>{r.revoked_reason}</small>}</span>], ["Issued", (r) => date(r.issued_at)],
    ["Email", (r) => <span title={r.email_error || ""}><span className={`pill ${r.email_status === "sent" ? "published" : r.email_status === "failed" ? "failed" : "draft"}`} data-testid={`email-status-${r.certificate_id}`}>{r.email_status.replace("_", " ")}</span>
      {(r.email_status === "failed" || r.email_status === "not_sent") && <button className="text-link-button inline" data-testid={`resend-email-${r.certificate_id}`} onClick={() => resend(r.certificate_id)}>Resend</button>}</span>],
    ["", (r) => r.status === "valid"
      ? <button className="button outline compact" data-testid={`revoke-cert-${r.certificate_id}`} onClick={() => setRevoking(r.certificate_id)}>Revoke</button>
      : <button className="button outline compact" data-testid={`reinstate-cert-${r.certificate_id}`} onClick={() => reinstate(r.certificate_id)}>Reinstate</button>]]} />
    <Modal open={!!revoking} onClose={() => { setRevoking(null); setReason(""); }} title={`Revoke ${revoking}`} testId="revoke-dialog">
      <form className="admin-form" onSubmit={revoke}>
        <p className="muted small">The public verification page will show this certificate as revoked. The reason is visible only to admins.</p>
        <label className="form-field"><span>Reason</span><textarea data-testid="revoke-reason-input" rows={3} minLength={3} value={reason} onChange={(e) => setReason(e.target.value)} required /></label>
        <button className="button primary full" data-testid="revoke-confirm-button">Revoke certificate</button>
      </form>
    </Modal>
  </>;
};

export const AccessLogRecords = () => {
  const [rows] = useRecords("/admin/access-log");
  return <>
    <p className="admin-note" data-testid="access-log-note">Every refused attempt to open an admin page or call admin data is recorded here (kept for 180 days).</p>
    <Table title="Blocked admin access" testId="access-log" rows={rows} empty="No blocked attempts recorded." columns={[
      ["When", (r) => dateTime(r.at)], ["Account", (r) => r.email || <span className="pill">signed out</span>], ["Role", (r) => r.role || "—"],
      ["Where", (r) => <><span className="pill">{r.source}</span> <code>{r.path}</code></>], ["Address", (r) => <code>{r.ip}</code>]]} />
  </>;
};
